// /src/components/AddTradeOffer.js
import { db } from '../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { validateMessage } from '../utils/profanityFilter';

export const addTradeOffer = async ({
  matchId,
  initiatorId,
  responderId,
  initiatorPedalId,
  initiatorPedalIds,
  responderPedalId,
  cashAdjustmentType,
  cashAdjustmentAmount,
  note,
  initiatorPedalPrices,
  responderPedalPrice,
  initiatorPedalDetails,
  responderPedalDetail,
  pedalSnapshots,
  isBulkSend = false,
  /** When offer originated from a listingInterest thread (DirectOffer). */
  sourceInterestId = null,
  // initiatorEmail is accepted but unused — callers may still pass it
}) => {
  const resolvedPedalIds = initiatorPedalIds && initiatorPedalIds.length > 0
    ? initiatorPedalIds
    : [initiatorPedalId];
  const primaryPedalId = resolvedPedalIds[0];

  if (!initiatorId) throw new Error('Missing initiator user ID.');
  if (!responderId) throw new Error('Missing responder user ID.');
  if (!responderPedalId) throw new Error('Missing responder pedal ID.');
  if (!resolvedPedalIds.length || !resolvedPedalIds[0]) throw new Error('At least one initiator pedal is required.');
  if (initiatorId === responderId) {
    throw new Error('You cannot create a trade offer with yourself.');
  }

  const MAX_CASH_AMOUNT = 2000;
  const numericCash = Number(cashAdjustmentAmount) || 0;
  if (numericCash < 0) {
    throw new Error('Cash amount cannot be negative.');
  }
  if (numericCash > MAX_CASH_AMOUNT) {
    throw new Error(`Cash amount cannot exceed $${MAX_CASH_AMOUNT.toLocaleString()}.`);
  }

  const safeNote =
    typeof note === 'string' ? note.trim().slice(0, 500) : '';

  if (safeNote) {
    const validation = validateMessage(safeNote);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }
  }

  const offerMessage = generateOfferMessage(cashAdjustmentType, cashAdjustmentAmount);

  const cashFlowFields = {};
  if (cashAdjustmentType === 'offer') {
    cashFlowFields.cashFlow = {
      payer: initiatorId,
      receiver: responderId
    };
  } else if (cashAdjustmentType === 'ask') {
    cashFlowFields.cashFlow = {
      payer: responderId,
      receiver: initiatorId
    };
  }

  const offerSnapshot = {
    initiatorPedalIds: resolvedPedalIds,
    responderPedalId,
    initiatorPedalPrices: initiatorPedalPrices || {},
    responderPedalPrice: responderPedalPrice || 0,
    cashAmount: cashAdjustmentAmount || 0,
    cashType: cashAdjustmentType || 'straight',
    cashFlow: cashFlowFields.cashFlow || null,
    initiatorId,
    responderId,
    ...(initiatorPedalDetails && { initiatorPedalDetails }),
    ...(responderPedalDetail && { responderPedalDetail })
  };
  
  const tradeOfferDoc = await addDoc(collection(db, 'tradeOffers'), {
    matchId,
    fromUserId: initiatorId,
    toUserId: responderId,
    type: cashAdjustmentType,
    cashAmount: cashAdjustmentAmount,
    status: 'pending',
    initiatorPedalId: primaryPedalId,
    initiatorPedalIds: resolvedPedalIds,
    isMultiPedal: resolvedPedalIds.length > 1,
    responderPedalId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    offerUpdatedAt: serverTimestamp(),
    ...cashFlowFields,
    note: safeNote,
    ...(isBulkSend && { isBulkSend: true }),
    ...(sourceInterestId && { sourceInterestId }),
    ...(pedalSnapshots && { pedalSnapshots }),
    history: [
      {
        type: 'offer',
        from: initiatorId,
        message: `${offerMessage}${safeNote ? ` Note: ${safeNote}` : ''}`,
        timestamp: new Date(),
        offerSnapshot
      }
    ]
  });

  return tradeOfferDoc.id;
};

const generateOfferMessage = (type, amount) => {
  if (type === 'offer') return `Offer submitted! Offering $${amount}.`;
  if (type === 'ask') return `Offer submitted! Asking for $${amount}.`;
  return 'Offer submitted! Straight trade.';
};